import React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { getSupabaseBrowser } from './supabaseClient';
import RESTAURANT_DATA from './data/restaurant-translations.json';
import EmailGate from './components/EmailGate';

const CITY_ALIASES = {
  'new york city': 'new york',
  'nyc': 'new york',
  'la': 'los angeles',
  'sf': 'san francisco',
  'cdmx': 'mexico city',
};

function lookupRestaurants(cityName) {
  const key = cityName?.toLowerCase().trim() ?? '';
  return RESTAURANT_DATA[key] ?? RESTAURANT_DATA[CITY_ALIASES[key]];
}

const TASTE_TIERS = [
  { key: 'fine dining', label: 'Fine Dining' },
  { key: 'fast casual', label: 'Fast Casual' },
  { key: 'street food', label: 'Street Food' },
];

const DAY_COLORS = ['#c8a96e', '#7ec8a0', '#a06ec8', '#6ea8c8', '#c86e6e'];

function parseItineraryDays(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const days = [];
  let current = null;
  for (const line of lines) {
    if (/^DAY \d+/.test(line)) {
      if (current) days.push(current);
      current = { header: line, lines: [line], dayIdx: days.length };
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) days.push(current);
  return days;
}

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem('cm_session_id');
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem('cm_session_id', id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

const DEV_MODE = false;
console.log('DEV_MODE is:', DEV_MODE);

const MOCK_NEIGHBORHOODS = [
  { id: 'polanco', madrid: 'Polanco', nyc: 'Beverly Hills', copy: 'Upscale residential area with luxury shopping and fine dining.', tags: ['Luxury', 'Residential', 'Fine Dining'], vibeMatch: 92, safety: 'Very High', price: '$$$$$', night: 'Low', walk: 'High', lat: 19.4284, lng: -99.1962, x: 44, y: 30 },
  { id: 'condesa', madrid: 'Condesa', nyc: 'Silver Lake', copy: 'Hip neighborhood with Art Deco architecture and trendy cafes.', tags: ['Indie', 'Creative', 'Cafes'], vibeMatch: 89, safety: 'Very High', price: '$$$', night: 'High', walk: 'Very High', lat: 19.4119, lng: -99.1761, x: 52, y: 42 },
  { id: 'roma', madrid: 'Roma Norte', nyc: 'Los Feliz', copy: 'Bohemian streets with galleries, bookstores and craft cocktail bars.', tags: ['Bohemian', 'Art', 'Nightlife'], vibeMatch: 87, safety: 'High', price: '$$$', night: 'Very High', walk: 'Very High', lat: 19.4167, lng: -99.1647, x: 48, y: 38 },
  { id: 'coyoacan', madrid: 'Coyoacán', nyc: 'Venice Beach', copy: 'Historic colonial neighborhood with cobblestone streets and markets.', tags: ['Historic', 'Cultural', 'Markets'], vibeMatch: 84, safety: 'High', price: '$$', night: 'Medium', walk: 'High', lat: 19.3467, lng: -99.1617, x: 34, y: 58 },
  { id: 'doctores', madrid: 'Doctores', nyc: 'Downtown LA', copy: 'Urban working class neighborhood undergoing rapid gentrification.', tags: ['Emerging', 'Urban', 'Gritty'], vibeMatch: 75, safety: 'Medium', price: '$', night: 'Medium', walk: 'High', lat: 19.4167, lng: -99.1400, x: 62, y: 46 },
  { id: 'tepito', madrid: 'Tepito', nyc: 'Compton', copy: 'Dense working class barrio with strong community identity and street markets.', tags: ['Working Class', 'Markets', 'Authentic'], vibeMatch: 68, safety: 'Low', price: '$', night: 'Low', walk: 'Medium', lat: 19.4417, lng: -99.1283, x: 66, y: 34 },
  { id: 'napoles', madrid: 'Nápoles', nyc: 'Culver City', copy: 'Mid-range residential neighborhood popular with young professionals.', tags: ['Residential', 'Young', 'Local'], vibeMatch: 78, safety: 'High', price: '$$', night: 'Medium', walk: 'High', lat: 19.3983, lng: -99.1817, x: 41, y: 52 },
  { id: 'santalfe', madrid: 'Santa Fe', nyc: 'Century City', copy: 'Modern business district with glass towers and upscale malls.', tags: ['Business', 'Modern', 'Upscale'], vibeMatch: 71, safety: 'Very High', price: '$$$$', night: 'Low', walk: 'Low', lat: 19.3597, lng: -99.2617, x: 28, y: 62 },
];

const VIBE_QUIZ_SECTIONS = [
  {
    title: 'Energy & Atmosphere',
    cards: [
      { id: 'rooftop', label: 'Rooftop energy', sub: 'City lights, late nights', archetype: 'socialite', img: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&q=80' },
      { id: 'park', label: 'Park mornings', sub: 'Quiet, green, restorative', archetype: 'wanderer', img: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&q=80' },
      { id: 'market', label: 'Market Saturdays', sub: 'Local, slow, authentic', archetype: 'insider', img: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80' },
      { id: 'historic', label: 'Historic squares', sub: 'Architecture, legacy', archetype: 'historian', img: 'https://images.unsplash.com/photo-1565117087073-e91be4c7cf6e?w=600&q=80' },
    ],
  },
  {
    title: 'Food & Drink',
    cards: [
      { id: 'streetfood', label: 'Street food alleys', sub: 'No menu, point and eat', archetype: 'insider', img: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80' },
      { id: 'dining', label: 'Candlelit dining', sub: 'Craft cocktails, mood', archetype: 'socialite', img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80' },
      { id: 'coffee', label: 'Third wave coffee', sub: 'Slow mornings, laptops', archetype: 'insider', img: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&q=80' },
      { id: 'bars', label: 'Late night bars', sub: 'Strangers, stories, noise', archetype: 'socialite', img: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=600&q=80' },
    ],
  },
  {
    title: 'Culture & Discovery',
    cards: [
      { id: 'museum', label: 'Empty museums', sub: 'Art, silence, wonder', archetype: 'historian', img: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=600&q=80' },
      { id: 'vintage', label: 'Vintage shops', sub: 'Records, finds, character', archetype: 'insider', img: 'https://images.unsplash.com/photo-1532499016263-f2c3e89de9cd?w=600&q=80' },
    ],
  },
];

const INSIDER_TAGS = ['Indie', 'Creative', 'Local', 'Authentic', 'Emerging', 'Art', 'Multicultural'];
const SOCIALITE_TAGS = ['Nightlife', 'Entertainment', 'LGBTQ+', 'Tourist'];
const HISTORIAN_TAGS = ['Historic', 'Cultural', 'Architecture', 'Heritage', 'Museum'];
const WANDERER_TAGS = ['Park', 'Green', 'Residential', 'Families', 'Quiet'];

function tagMatchPoints(tags, allowed, perMatch) {
  let s = 0;
  const allowedLc = allowed.map((a) => a.toLowerCase());
  for (const t of tags || []) {
    const tl = String(t).toLowerCase();
    if (allowedLc.includes(tl)) s += perMatch;
  }
  return s;
}

function tasteInsiderComponent(n) {
  let s = 0;
  if (n.walk === 'Very High') s += 2;
  s += tagMatchPoints(n.tags, INSIDER_TAGS, 2);
  if (n.night === 'Low' || n.night === 'Medium') s += 1;
  return s;
}

function tasteSocialiteComponent(n) {
  let s = 0;
  if (n.night === 'Very High') s += 3;
  else if (n.night === 'High') s += 2;
  s += tagMatchPoints(n.tags, SOCIALITE_TAGS, 2);
  return s;
}

function tasteHistorianComponent(n) {
  return tagMatchPoints(n.tags, HISTORIAN_TAGS, 2);
}

function tasteWandererComponent(n) {
  let s = tagMatchPoints(n.tags, WANDERER_TAGS, 2);
  if (n.night === 'Low') s += 1;
  return s;
}

function archetypeWeightVector(tasteProfile) {
  const ins = tasteProfile.insider || 0;
  const soc = tasteProfile.socialite || 0;
  const his = tasteProfile.historian || 0;
  const wan = tasteProfile.wanderer || 0;
  const t = ins + soc + his + wan;
  if (t === 0) return { insider: 0, socialite: 0, historian: 0, wanderer: 0 };
  return { insider: ins / t, socialite: soc / t, historian: his / t, wanderer: wan / t };
}

function neighborhoodTasteScore(n, w) {
  return (
    w.insider * tasteInsiderComponent(n) +
    w.socialite * tasteSocialiteComponent(n) +
    w.historian * tasteHistorianComponent(n) +
    w.wanderer * tasteWandererComponent(n)
  );
}

/** Re-ranks a copy of `neighborhoods` by taste match when `tasteProfile` is set. */
function getRankedNeighborhoods(neighborhoods, tasteProfile) {
  if (!Array.isArray(neighborhoods)) return [];
  if (!tasteProfile) return [...neighborhoods];
  const w = archetypeWeightVector(tasteProfile);
  return [...neighborhoods]
    .map((n) => ({ n, tasteScore: neighborhoodTasteScore(n, w) }))
    .sort((a, b) => b.tasteScore - a.tasteScore)
    .map(({ n }) => n);
}

/** Same order as `ordered`; adds `vibeFit` (0–100) from taste scores vs max when `tasteProfile` is set. */
function attachVibeFitToOrdered(orderedNeighborhoods, allNeighborhoods, tasteProfile) {
  if (!tasteProfile) return orderedNeighborhoods.map((n) => ({ ...n }));
  const w = archetypeWeightVector(tasteProfile);
  let maxScore = 0;
  const tasteById = {};
  for (const n of allNeighborhoods) {
    const s = neighborhoodTasteScore(n, w);
    tasteById[n.id] = s;
    if (s > maxScore) maxScore = s;
  }
  return orderedNeighborhoods.map((n) => ({
    ...n,
    vibeFit: maxScore > 0 ? Math.round((tasteById[n.id] / maxScore) * 100) : 0,
  }));
}

function SidebarVibeBadges({ vibeMatch, vibeFit, tasteProfileActive }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8, alignItems: 'flex-start' }}>
      <div style={{
        borderRadius: 3,
        padding: '2px 6px',
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.2,
        ...(tasteProfileActive
          ? { background: 'transparent', color: '#3a3530', border: '1px solid #2a2825' }
          : { background: 'rgba(200,169,110,0.12)', color: '#c8a96e', border: '1px solid rgba(200,169,110,0.3)' }),
      }}
      >
        {vibeMatch}%
      </div>
      <div style={{
        display: tasteProfileActive ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        padding: '2px 6px 3px',
        background: 'rgba(200,169,110,0.12)',
        border: '1px solid rgba(200,169,110,0.3)',
      }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: '#c8a96e', lineHeight: 1.2 }}>{vibeFit}%</span>
        <span style={{
          fontSize: 8,
          fontWeight: 500,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#c8a96e',
          marginTop: 1,
        }}>
          fit
        </span>
      </div>
    </div>
  );
}

const ARCHETYPE_INSIGHT = {
  insider: 'You gravitate toward local rhythm—markets, regular tables, and neighborhoods that feel lived-in rather than staged.',
  socialite: 'You chase energy after dark—cocktails, rooftops, and districts where the city turns up the volume.',
  historian: 'You read cities through their layers—squares, museums, and streets that still echo older chapters.',
  wanderer: 'You need room to breathe—green pockets, slower mornings, and places where wandering is the point.',
};

const ARCHETYPE_BARS = [
  { key: 'insider', label: 'Insider', desc: 'Local tables, markets, third places' },
  { key: 'socialite', label: 'Socialite', desc: 'Nights out, mood, crowd energy' },
  { key: 'historian', label: 'Historian', desc: 'Legacy, culture, quiet wonder' },
  { key: 'wanderer', label: 'Wanderer', desc: 'Green space, pace, restorative drift' },
];

/** [lng, lat] for Mapbox — extend as needed for translateCity values */
const CITY_CENTER_LNGLAT = {
  madrid: [-3.7038, 40.4168],
  barcelona: [2.1734, 41.3851],
  paris: [2.3522, 48.8566],
  london: [-0.1276, 51.5074],
  berlin: [13.405, 52.52],
  rome: [12.4964, 41.9028],
  lisbon: [-9.1393, 38.7223],
  amsterdam: [4.9041, 52.3676],
  'mexico city': [-99.1332, 19.4326],
  'new york': [-74.006, 40.7128],
  tokyo: [139.6917, 35.6895],
  sydney: [151.2093, -33.8688],
};

function getCityCenter(cityName) {
  if (!cityName || typeof cityName !== 'string') return null;
  const k = cityName.toLowerCase().trim();
  return CITY_CENTER_LNGLAT[k] ?? null;
}

/** Matches `scripts/precompute-mappings.js` upsert keys for `precomputed_mappings`. */
function normalizedCityPair(homeCity, translateCity) {
  return {
    home: String(homeCity ?? '').toLowerCase().trim(),
    translate: String(translateCity ?? '').toLowerCase().trim(),
  };
}

/** Compact pill label e.g. multi-word cities → initials, single word → first 3 letters */
function abbrevCityPair(homeCity, translateCity) {
  const abbr = (s) => {
    const words = String(s).trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return words.map((w) => w[0]).join('').toUpperCase().slice(0, 4);
    return String(s).trim().slice(0, 3).toUpperCase();
  };
  return `${abbr(homeCity)} → ${abbr(translateCity)}`;
}

function vibeCircleOpacity(vibeMatch) {
  const v = Number(vibeMatch);
  if (Number.isNaN(v)) return 0.05;
  if (v >= 90) return 0.3;
  if (v >= 80) return 0.18;
  if (v >= 70) return 0.1;
  return 0.05;
}

function getNeighborhoodPhotos(neighborhoodName, cityName) {
  const clientId = process.env.REACT_APP_UNSPLASH_KEY;
  if (!clientId) return '';
  const n = encodeURIComponent(String(neighborhoodName)).replace(/%20/g, '+');
  const c = encodeURIComponent(String(cityName)).replace(/%20/g, '+');
  return `https://api.unsplash.com/search/photos?query=${n}+${c}+neighborhood&per_page=3&orientation=landscape&client_id=${encodeURIComponent(clientId)}`;
}

const S_COLOR = {
  'Very High': '#7ec8a0',
  High: '#a8c87e',
  Medium: '#c8b96e',
  Low: '#c8846e',
};
const S_BG = {
  'Very High': 'rgba(126,200,160,0.1)',
  High: 'rgba(168,200,126,0.1)',
  Medium: 'rgba(200,185,110,0.1)',
  Low: 'rgba(200,132,110,0.1)',
};
const S_BORD = {
  'Very High': 'rgba(126,200,160,0.25)',
  High: 'rgba(168,200,126,0.25)',
  Medium: 'rgba(200,185,110,0.25)',
  Low: 'rgba(200,132,110,0.25)',
};

const SAFETY_CIRCLE_BG = {
  'Very High': 'rgba(126,200,160,0.18)',
  High: 'rgba(168,200,126,0.15)',
  Medium: 'rgba(200,185,110,0.12)',
  Low: 'rgba(200,132,110,0.12)',
};

function VibeMeter({ value }) {
  const r = 21, circ = 2 * Math.PI * r;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49' }}>
        Vibe Match
      </div>
      <div style={{ position: 'relative', width: 54, height: 54 }}>
        <svg width="54" height="54" viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="27" cy="27" r={r} fill="none" stroke="#2a2825" strokeWidth="3" />
          <circle cx="27" cy="27" r={r} fill="none" stroke="#c8a96e" strokeWidth="3"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - value / 100)}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.16,1,0.3,1)' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Playfair Display', serif", fontSize: 13, fontWeight: 700, color: '#c8a96e',
        }}>
          {value}%
        </div>
      </div>
    </div>
  );
}

function normalizeSearchResult(r, searchedNeighborhood, direction) {
  return {
    id: '__search__',
    madrid: r.name,
    // from_home: searched neighborhood is the home ref; from_destination: API provides home_equivalent
    nyc: direction === 'from_destination' ? (r.home_equivalent || searchedNeighborhood) : searchedNeighborhood,
    copy: r.why_it_matches,
    tags: Array.isArray(r.vibe_tags) ? r.vibe_tags : [],
    vibeMatch: 95,
    safety: r.safety,
    price: r.price,
    night: r.nightlife,
    walk: r.walkability,
    lat: Number(r.lat),
    lng: Number(r.lng),
    isSearchResult: true,
    searchDirection: direction,
  };
}

export default function CityMirror({ homeCity, translateCity, onBack }) {
  console.log('DEV_MODE value:', DEV_MODE);
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapMode, setMapMode] = useState('vibe');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInstanceKey, setMapInstanceKey] = useState(0);
  const [overlayPx, setOverlayPx] = useState({});
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const searchMarkerRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showVibeQuiz, setShowVibeQuiz] = useState(false);
  const [vibeSelections, setVibeSelections] = useState({});
  const [archetypeScores, setArchetypeScores] = useState({ insider: 0, socialite: 0, historian: 0, wanderer: 0 });
  const [tasteProfile, setTasteProfile] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sheetState, setSheetState] = useState('list'); // 'list' or 'detail'
  const [detailTab, setDetailTab] = useState('vibe'); // 'vibe' or 'eats'
  const [itineraryPhase, setItineraryPhase] = useState(null); // null | 'generating' | 'display' | 'error'
  const [itinerary, setItinerary] = useState('');
  const [itineraryError, setItineraryError] = useState('');
  const [itineraryExpanded, setItineraryExpanded] = useState(false);
  const [itineraryActiveDay, setItineraryActiveDay] = useState(0);
  const [itineraryDayMap, setItineraryDayMap] = useState([]);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [pendingRanked, setPendingRanked] = useState(null);
  const [itineraryId, setItineraryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDirection, setSearchDirection] = useState('from_home'); // 'from_home' | 'from_destination'
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [searchOverlayPx, setSearchOverlayPx] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (active) setSheetState('detail');
    else setSheetState('list');
  }, [isMobile, active]);

  const vibeQuizRef = useRef({ vibeSelections, archetypeScores });
  vibeQuizRef.current = { vibeSelections, archetypeScores };
  const itineraryCardRef = useRef(null);

  const select = useCallback((id) => {
    setActive((p) => (p === id ? null : id));
  }, []);

  useEffect(() => {
    setFeedbackRating(0);
    setFeedbackText('');
    setFeedbackSubmitted(false);
    setDetailTab('vibe');
  }, [active]);

  const toggleVibeCard = useCallback((card) => {
    const { vibeSelections: vs } = vibeQuizRef.current;
    const turningOn = !vs[card.id];
    setVibeSelections((prevSel) => {
      if (turningOn) return { ...prevSel, [card.id]: true };
      const next = { ...prevSel };
      delete next[card.id];
      return next;
    });
    setArchetypeScores((prevSc) => ({
      ...prevSc,
      [card.archetype]: Math.max(0, prevSc[card.archetype] + (turningOn ? 1 : -1)),
    }));
  }, []);

  const handleUpdateVibeResults = useCallback(() => {
    const profile = { ...archetypeScores };
    setTasteProfile(profile);
    const ranked = getRankedNeighborhoods(neighborhoods, profile);
    if (ranked[0]) setActive(ranked[0].id);
    setShowVibeQuiz(false);
  }, [neighborhoods, archetypeScores]);

  const sidebarNeighborhoods = useMemo(
    () => (tasteProfile ? getRankedNeighborhoods(neighborhoods, tasteProfile) : neighborhoods),
    [neighborhoods, tasteProfile],
  );

  const sidebarListNeighborhoods = useMemo(
    () => attachVibeFitToOrdered(sidebarNeighborhoods, neighborhoods, tasteProfile),
    [sidebarNeighborhoods, neighborhoods, tasteProfile],
  );

  useEffect(() => {
    if (DEV_MODE) {
      console.log('Skipping API, using mock data');
      setNeighborhoods(MOCK_NEIGHBORHOODS);
      if (MOCK_NEIGHBORHOODS.length > 0) setActive(MOCK_NEIGHBORHOODS[0].id);
      return;
    }

    const fetchNeighborhoods = async () => {
      setLoading(true);
      let cachedResult = null;
      try {
        const { home, translate } = normalizedCityPair(homeCity, translateCity);
        const supabase = getSupabaseBrowser();

        console.log('[CityMirror] cache lookup →', { home_city: home, translate_city: translate, supabaseReady: !!supabase });

        if (supabase && home && translate) {
          const { data: cached, error: cacheReadErr } = await supabase
            .from('precomputed_mappings')
            .select('neighborhoods')
            .eq('home_city', home)
            .eq('translate_city', translate)
            .maybeSingle();

          if (cacheReadErr) {
            console.error('Supabase cache read:', cacheReadErr);
          } else if (cached) {
            const raw = cached.neighborhoods;
            let list = null;
            if (Array.isArray(raw)) list = raw;
            else if (typeof raw === 'string') {
              try {
                list = JSON.parse(raw);
              } catch {
                list = null;
              }
            }
            if (Array.isArray(list) && list.length > 0) {
              cachedResult = list;
              return;
            }
          }
        }

        // Cache miss — log so the nightly precompute job can discover this pair
        if (supabase && home && translate) {
          supabase.from('search_log').insert({ home_city: home, translate_city: translate })
            .then(({ error }) => { if (error) console.warn('search_log insert:', error.message); });
        }

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-6',
            max_tokens: 4000,
            messages: [{
              role: 'user',
              content: `Compare ${translateCity} neighborhoods to ${homeCity} 
neighborhoods. Return ONLY a JSON array, no markdown, no explanation, 
just raw JSON. Important instructions:
- Return exactly 12 neighborhoods
- Go beyond obvious major districts, include lesser-known 
  sub-neighborhoods and local pockets that locals actually live in
- For New York as homeCity, draw from ALL boroughs including Queens 
  (Astoria, Flushing, Jackson Heights, Long Island City, Ridgewood), 
  Brooklyn (Greenpoint, Crown Heights, Bed-Stuy, Ditmas Park, Bay Ridge, 
  Bushwick, Carroll Gardens), Manhattan, and the Bronx
- For other home cities, go beyond tourist-facing districts into where 
  real residents live
- Match energy, price point, demographics, walkability and vibe
- Include a mix of upscale, mid-range, and emerging neighborhoods

Each object must have exactly these fields: id (lowercase no spaces), 
madrid (neighborhood name in ${translateCity}), nyc (equivalent in 
${homeCity}), copy (2-3 sentence description), tags (array of 3 strings), 
vibeMatch (number 70-98), safety (one of: Very High, High, Medium, Low), 
price ($ to $$$$$), night (Low/Medium/High/Very High), walk 
(Low/Medium/High/Very High), lat (latitude as decimal), lng (longitude 
as decimal). Return exactly 12 neighborhoods.`,
            }],
          }),
        });

        const data = await response.json();
        const text = data.content[0].text;
        const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        const parsed = JSON.parse(cleaned);
        setNeighborhoods(parsed);
        if (parsed.length > 0) setActive(parsed[0].id);
      } catch (err) {
        console.error('API error:', err);
      } finally {
        if (cachedResult) {
          await new Promise((r) => setTimeout(r, 1500));
          setNeighborhoods(cachedResult);
          if (cachedResult[0]?.id) setActive(cachedResult[0].id);
        }
        setLoading(false);
      }
    };
    fetchNeighborhoods();
  }, [homeCity, translateCity]);

  /** Loading UI replaces the map subtree; destroy Mapbox so we can attach to the new container after load. */
  useEffect(() => {
    if (!loading) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    if (map.current) {
      map.current.remove();
      map.current = null;
      setMapInstanceKey((k) => k + 1); // forces React to remount the container div → fresh empty node for Mapbox
    }
    setMapLoaded(false);
  }, [loading]);

  useEffect(() => {
    if (loading || neighborhoods.length === 0 || !mapContainer.current) return;

    if (map.current) {
      try {
        if (map.current.getContainer() !== mapContainer.current) {
          map.current.remove();
          map.current = null;
          setMapLoaded(false);
        }
      } catch {
        map.current = null;
        setMapLoaded(false);
      }
    }

    const first = neighborhoods[0];
    const fromTable = getCityCenter(translateCity);
    const center =
      fromTable ??
      (first?.lng != null && first?.lat != null
        ? [Number(first.lng), Number(first.lat)]
        : [-99.1332, 19.4326]);

    const clearMarkers = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    };

    const addMarkers = () => {
      if (!map.current) return;
      clearMarkers();
      neighborhoods.forEach((n) => {
        if (n.lat == null || n.lng == null) return;
        const el = document.createElement('div');
        el.style.cssText =
          'width:10px;height:10px;border-radius:50%;background:#c8a96e;cursor:pointer;flex-shrink:0;';
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([Number(n.lng), Number(n.lat)])
          .addTo(map.current);
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          select(n.id);
        });
        markersRef.current.push(marker);
      });
    };

    if (!map.current) {
      mapboxgl.accessToken =
        'pk.eyJ1IjoicnVkcm9iaXN3YXMyNSIsImEiOiJjbW40eTFweG8wNWswMnJxNTQxMWtndnppIn0.xi9dcMmeQ_cVxQVMaV6eSg';
      // Clear any leftover Mapbox DOM from a previous instance (avoids "container is not empty" warning)
      const initialZoom = typeof window !== 'undefined' && window.innerWidth < 768 ? 11 : 12;
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center,
        zoom: initialZoom,
        attributionControl: false,
      });
      map.current.on('load', () => {
        map.current.resize();
        setMapLoaded(true);
        addMarkers();
      });
    } else {
      map.current.flyTo({ center, essential: true });
      if (map.current.loaded()) {
        map.current.resize();
        addMarkers();
      } else {
        map.current.once('load', () => {
          map.current.resize();
          addMarkers();
        });
      }
    }
  }, [neighborhoods, translateCity, select, isMobile, loading]);

  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setMapLoaded(false);
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded || !map.current || neighborhoods.length === 0) return;

    const updateOverlayPx = () => {
      const m = map.current;
      if (!m) return;
      const next = {};
      neighborhoods.forEach((n) => {
        if (n.lng == null || n.lat == null) return;
        const p = m.project([Number(n.lng), Number(n.lat)]);
        next[n.id] = { left: p.x, top: p.y };
      });
      setOverlayPx(next);
    };

    updateOverlayPx();
    const m = map.current;
    m.on('move', updateOverlayPx);
    m.on('zoom', updateOverlayPx);
    m.on('rotate', updateOverlayPx);
    m.on('pitch', updateOverlayPx);
    m.on('resize', updateOverlayPx);
    m.once('idle', updateOverlayPx);

    return () => {
      m.off('move', updateOverlayPx);
      m.off('zoom', updateOverlayPx);
      m.off('rotate', updateOverlayPx);
      m.off('pitch', updateOverlayPx);
      m.off('resize', updateOverlayPx);
    };
  }, [neighborhoods, mapLoaded]);

  const activeN = active === '__search__'
    ? searchResult
    : neighborhoods.find((n) => n.id === active);

  const handleFeedbackSubmit = useCallback(async () => {
    if (!activeN) return;
    try {
      await fetch('https://formspree.io/f/mbdpvbbr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          neighborhood: activeN.madrid,
          city: translateCity,
          homeCity: homeCity,
          rating: feedbackRating,
          comment: feedbackText,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error('Feedback error:', err);
    }
    setFeedbackSubmitted(true);
  }, [activeN, translateCity, homeCity, feedbackRating, feedbackText]);

  const generateItinerary = useCallback(async (rankedNeighborhoods, email) => {
    try {
      setItineraryError('');
      // Assign top 6 neighborhoods to 3 days (2 per day)
      const numDays = 3;
      const dayMap = [];
      for (let d = 0; d < numDays; d++) {
        const slice = (rankedNeighborhoods || []).slice(d * 2, d * 2 + 2);
        if (slice.length > 0) dayMap.push({ dayIdx: d, neighborhoods: slice });
      }
      setItineraryDayMap(dayMap);
      setItineraryActiveDay(0);
      setItineraryPhase('generating');

      await new Promise((r) => setTimeout(r, 1500));

      const totalArchetype =
        archetypeScores.insider + archetypeScores.socialite +
        archetypeScores.historian + archetypeScores.wanderer;
      const dominantKey = totalArchetype > 0
        ? ['insider', 'socialite', 'historian', 'wanderer'].reduce(
            (best, k) => archetypeScores[k] > archetypeScores[best] ? k : best,
            'insider')
        : 'wanderer';

      const dayDescriptions = dayMap.map((day, i) => {
        const names = day.neighborhoods
          .map((n) => `${n.madrid} (like ${n.nyc} in ${homeCity})`)
          .join(' & ');
        return `Day ${i + 1}: ${names}`;
      }).join('\n');

      const prompt = `You are a luxury travel writer. Create a personalized itinerary for someone who knows ${homeCity} well but is visiting ${translateCity} for the first time. Their traveler archetype is: ${dominantKey}.

Use exactly these neighborhood assignments (do not change them):
${dayDescriptions}

Write a 3-day itinerary in this EXACT format — no deviations:

DAY 1 — [Evocative Title]
[Primary Neighborhood] & [Secondary Neighborhood]
[One intro paragraph, 2–3 sentences. Day 1 only: reference a ${homeCity} landmark or feeling to orient the reader.]

[Time e.g. "9:00 AM"]  [Place Name] · [Neighborhood]
[One editorial sentence of local detail.]

[4–5 more time entries in the same format]

---

DAY 2 — [Evocative Title]
[Primary Neighborhood] & [Secondary Neighborhood]

[4–6 time entries]

---

DAY 3 — [Evocative Title]
[Primary Neighborhood] & [Secondary Neighborhood]

[4–6 time entries]

End the entire itinerary with this exact line on its own paragraph:
Safe travels. You know this city now.

Rules: No bullet points. No extra headers. Time entries must match the pattern "[Time]  [Place] · [Neighborhood]" exactly (two spaces between time and place). Write with warmth and the confidence of a local.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 4000,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message =
          errorBody?.error?.message ||
          errorBody?.message ||
          response.statusText ||
          'Unknown Anthropic API error';
        throw new Error(`Anthropic API request failed (${response.status}): ${message}`);
      }

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Anthropic API response did not include itinerary text.');
      }
      const text = data.content[0].text;
      setItinerary(text);
      setItineraryPhase('display');

      // Persist itinerary to Supabase
      try {
        const supabase = getSupabaseBrowser();
        if (supabase) {
          const { data: insertedRow, error: insertError } = await supabase
            .from('itineraries')
            .insert({
              home_city: homeCity,
              destination_city: translateCity,
              itinerary_data: { text },
              email: null,
              session_id: getOrCreateSessionId(),
            })
            .select()
            .single();

          if (insertError) {
            console.error('itineraries insert error:', insertError.message);
          } else {
            const newId = insertedRow?.id;
            setItineraryId(newId);

            if (
              typeof window !== 'undefined' &&
              window.innerWidth < 768 &&
              newId
            ) {
              navigate(`/itinerary/${newId}`);
            }

            // Send email via Edge Function if user provided one
            if (email && newId) {
              try {
                const res = await fetch(
                  'https://echilasdwsvksxhzuxgc.supabase.co/functions/v1/send-itinerary-email',
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                    },
                    body: JSON.stringify({
                      email,
                      itinerary_id: newId,
                      home_city: homeCity,
                      destination_city: translateCity,
                    }),
                  }
                );
                if (res.ok) {
                  console.log('send-itinerary-email: success');
                } else {
                  const body = await res.json().catch(() => ({}));
                  console.error('send-itinerary-email: non-OK response', body);
                }
              } catch (emailErr) {
                console.error('send-itinerary-email: fetch error', emailErr);
              }
            }
          }
        }
      } catch (persistErr) {
        console.error('itinerary persist error:', persistErr);
      }
    } catch (err) {
      console.error('Itinerary generation error:', err);
      setItineraryError("We couldn't generate your itinerary right now. Please try again in a moment.");
      setItineraryPhase('error');
    }
  }, [homeCity, translateCity, archetypeScores, navigate]);

  const handleBuildItinerary = useCallback(() => {
    const profile = { ...archetypeScores };
    setTasteProfile(profile);
    const ranked = getRankedNeighborhoods(neighborhoods, profile);
    if (ranked[0]) setActive(ranked[0].id);
    setShowVibeQuiz(false);
// Store ranked neighborhoods and open the email gate; generation fires from onProceed
setPendingRanked(ranked);
setShowEmailGate(true);
}, [neighborhoods, archetypeScores]);

  // Clear search when city pair changes
  useEffect(() => {
    setSearchResult(null);
    setSearchError('');
    setSearchQuery('');
    setSearchDirection('from_home');
    setSearchOverlayPx(null);
    if (searchMarkerRef.current) {
      searchMarkerRef.current.remove();
      searchMarkerRef.current = null;
    }
  }, [homeCity, translateCity]);

  const handleNeighborhoodSearch = useCallback(async (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);

    try {
      const supabase = getSupabaseBrowser();
      const homeLower = homeCity.toLowerCase().trim();
      const destLower = translateCity.toLowerCase().trim();
      const searchLower = trimmed.toLowerCase();
      const direction = searchDirection;

      // Cache lookup — keyed by direction so both modes cache independently
      if (supabase) {
        const { data: cached } = await supabase
          .from('neighborhood_searches')
          .select('result')
          .eq('home_city', homeLower)
          .eq('destination_city', destLower)
          .eq('searched_neighborhood', searchLower)
          .eq('search_direction', direction)
          .maybeSingle();
        if (cached?.result) {
          const normalized = normalizeSearchResult(cached.result, trimmed, direction);
          setSearchResult(normalized);
          setActive('__search__');
          setSearchLoading(false);
          return;
        }
      }

      // Build prompt based on direction
      const prompt = direction === 'from_destination'
        ? `I'm from ${homeCity} visiting ${translateCity}. I've heard of ${trimmed} in ${translateCity}. Explain what it's like using ${homeCity} neighborhoods as reference points. Return ONLY a JSON object (no markdown, no explanation) with exactly these fields: name (string — must be "${trimmed}"), home_equivalent (string — the closest neighborhood in ${homeCity}), why_it_matches (string — 2–3 sentences explaining what ${trimmed} is like in ${homeCity} terms), vibe_tags (array of exactly 3 strings), safety (one of: Very High, High, Medium, Low), price ($ to $$$$$), nightlife (one of: Low, Medium, High, Very High), walkability (one of: Low, Medium, High, Very High), lat (number — latitude of ${trimmed} in ${translateCity}), lng (number — longitude of ${trimmed} in ${translateCity}). If ${trimmed} is not a real neighborhood in ${translateCity}, return {"error":"not_found"}.`
        : `I'm from ${homeCity} and know ${trimmed} well. What is the equivalent neighborhood in ${translateCity}? Return ONLY a JSON object (no markdown, no explanation) with exactly these fields: name (string — the neighborhood name in ${translateCity}), why_it_matches (string — 2–3 sentences explaining the match), vibe_tags (array of exactly 3 strings), safety (one of: Very High, High, Medium, Low), price ($ to $$$$$), nightlife (one of: Low, Medium, High, Very High), walkability (one of: Low, Medium, High, Very High), lat (number), lng (number). If ${trimmed} is not a real neighborhood in ${homeCity} or you cannot make a confident match, return {"error":"not_found"}.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      const data = await response.json();
      const raw = data.content[0].text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(raw);

      if (parsed.error === 'not_found') {
        setSearchError(trimmed);
        return;
      }

      // Cache result with direction
      if (supabase) {
        supabase
          .from('neighborhood_searches')
          .insert({ home_city: homeLower, destination_city: destLower, searched_neighborhood: searchLower, search_direction: direction, result: parsed })
          .then(({ error }) => { if (error) console.warn('search cache insert:', error.message); });
      }

      const normalized = normalizeSearchResult(parsed, trimmed, direction);
      setSearchResult(normalized);
      setActive('__search__');
    } catch (err) {
      console.error('Search error:', err);
      setSearchError(trimmed);
    } finally {
      setSearchLoading(false);
    }
  }, [homeCity, translateCity, searchDirection]);

  const handleGateProceed = useCallback((email) => {
    setShowEmailGate(false);
    setTimeout(() => generateItinerary(pendingRanked, email || null), 100);
  }, [generateItinerary, pendingRanked]);

  const handleGateClose = useCallback(() => {
    setShowEmailGate(false);
  }, []);
  const renderItineraryLines = (text, compact = false) =>
    text.split('\n').map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: compact ? 6 : 10 }} />;
      if (line === '---') return <div key={i} style={{ height: 1, background: '#2a2825', margin: compact ? '16px 0' : '24px 0' }} />;
      if (line === 'Safe travels. You know this city now.') {
        return (
          <div key={i} style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: compact ? 14 : 18, fontStyle: 'italic',
            color: '#c8a96e', marginTop: compact ? 20 : 32, textAlign: 'center', lineHeight: 1.4,
          }}>
            {line}
          </div>
        );
      }
      if (/^DAY \d+/.test(line)) {
        return (
          <div key={i} style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: compact ? 16 : 22, fontWeight: 700,
            color: '#f0ebe3', marginTop: compact ? 22 : 36, marginBottom: 3, lineHeight: 1.2,
          }}>
            {line}
          </div>
        );
      }
      if (/^\d+:\d+/.test(line) || /^(Morning|Afternoon|Evening|Night) —/.test(line)) {
        return (
          <div key={i} style={{
            fontSize: compact ? 11 : 13, fontWeight: 600, color: '#c8a96e',
            marginTop: compact ? 12 : 18, marginBottom: 2, letterSpacing: '0.02em',
          }}>
            {line}
          </div>
        );
      }
      return (
        <p key={i} style={{ fontSize: compact ? 12 : 13, lineHeight: 1.75, color: '#8a8278', margin: '0 0 2px' }}>
          {line}
        </p>
      );
    });

  useEffect(() => {
    if (!activeN?.madrid) {
      setPhotos([]);
      setPhotoIndex(0);
      return;
    }
    let cancelled = false;
    const url = getNeighborhoodPhotos(activeN.madrid, translateCity);
    if (!url) {
      setPhotos([]);
      setPhotoIndex(0);
      return;
    }
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('unsplash');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const results = Array.isArray(data?.results) ? data.results : [];
        const urls = results
          .slice(0, 3)
          .map((p) => p?.urls?.regular || p?.urls?.small)
          .filter(Boolean);
        setPhotos(urls);
        setPhotoIndex(0);
      })
      .catch(() => {
        if (!cancelled) {
          setPhotos([]);
          setPhotoIndex(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeN?.id, activeN?.madrid, translateCity]);

  useEffect(() => {
    if (itineraryPhase !== 'display' || !map.current || itineraryDayMap.length === 0) return;
    const day = itineraryDayMap[itineraryActiveDay];
    if (!day || day.neighborhoods.length === 0) return;
    const lngs = day.neighborhoods.map((n) => Number(n.lng)).filter(Boolean);
    const lats = day.neighborhoods.map((n) => Number(n.lat)).filter(Boolean);
    if (lngs.length === 0) return;
    const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
    const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    map.current.easeTo({ center: [centerLng, centerLat], duration: 800, essential: true });
  }, [itineraryActiveDay, itineraryPhase, itineraryDayMap]);

  if (loading) {
    return (
      <div style={{
        background: '#0e0d0c', height: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20,
      }}>
        <style>{`@keyframes cmPulse{0%,100%{opacity:.25;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}`}</style>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#f0ebe3' }}>
          City<span style={{ color: '#c8a96e' }}>Mirror</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#c8a96e', animation: `cmPulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
          ))}
        </div>
        <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#524e49' }}>
          Translating {translateCity}…
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#2a2825;border-radius:2px}
        .detail-card-scroll{scrollbar-width:thin;scrollbar-color:#2a2825 transparent}
        .detail-card-scroll::-webkit-scrollbar{width:3px}
        .detail-card-scroll::-webkit-scrollbar-track{background:transparent}
        .detail-card-scroll::-webkit-scrollbar-thumb{background:#2a2825;border-radius:2px}
        @keyframes cardIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes cmPulse{0%,100%{opacity:.25;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}
        .nitem:hover{background:#161512!important}
        .tbtn:hover{color:#f0ebe3!important}
      `}</style>

      {showVibeQuiz && (() => {
        const selectedCount = Object.keys(vibeSelections).length;
        const progressPct = Math.min(selectedCount / 5, 1) * 100;
        const totalArchetype =
          archetypeScores.insider +
          archetypeScores.socialite +
          archetypeScores.historian +
          archetypeScores.wanderer;
        const dominantKey =
          totalArchetype > 0
            ? ['insider', 'socialite', 'historian', 'wanderer'].reduce((best, k) =>
                archetypeScores[k] > archetypeScores[best] ? k : best,
              'insider')
            : 'insider';

        return (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 110,
            background: '#0e0d0c',
            overflowY: 'auto',
          }}>
            <button
              type="button"
              onClick={() => setShowVibeQuiz(false)}
              aria-label="Close vibe quiz"
              style={{
                position: 'absolute',
                top: 20,
                right: 28,
                fontSize: 20,
                color: '#8a8278',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                zIndex: 2,
              }}
            >
              ×
            </button>

            <div style={{
              maxWidth: 720,
              margin: '0 auto',
              padding: '72px 24px 56px',
              fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
            }}>
              <div style={{ marginBottom: 28 }}>
                <div style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#524e49',
                  marginBottom: 12,
                }}>
                  Taste Fingerprint
                </div>
                <h1 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#f0ebe3',
                  lineHeight: 1.2,
                  margin: '0 0 14px',
                }}>
                  What kind of traveler{' '}
                  <em style={{ fontStyle: 'italic', color: '#c8a96e' }}>are you?</em>
                </h1>
                <p style={{ fontSize: 13, color: '#8a8278', lineHeight: 1.55, margin: '0 0 12px' }}>
                  Tap the vibes that feel like you. No right answers — just moments that make a neighborhood feel like yours.
                </p>
                <div style={{ fontSize: 11, color: '#524e49' }}>
                  Takes 60 seconds · Select as many as you want
                </div>
              </div>

              <div style={{ width: '100%', height: 2, background: '#2a2825', borderRadius: 1, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: '#c8a96e',
                  transition: 'width 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                }} />
              </div>
              {selectedCount > 0 && (
                <div style={{ fontSize: 12, color: '#c8a96e', marginBottom: 24 }}>
                  {selectedCount} vibe{selectedCount === 1 ? '' : 's'} selected
                </div>
              )}
              {selectedCount === 0 && <div style={{ height: 8, marginBottom: 16 }} />}

              {VIBE_QUIZ_SECTIONS.map((section) => (
                <div key={section.title} style={{ marginBottom: 28 }}>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#524e49',
                    marginBottom: 12,
                  }}>
                    {section.title}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                  }}>
                    {section.cards.map((card) => {
                      const selected = !!vibeSelections[card.id];
                      return (
                        <button
                          key={card.id}
                          type="button"
                          onClick={() => toggleVibeCard(card)}
                          style={{
                            position: 'relative',
                            height: 200,
                            borderRadius: 6,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'block',
                            width: '100%',
                            border: selected ? '2px solid #c8a96e' : '1px solid #2a2825',
                            background: '#161512',
                            textAlign: 'left',
                          }}
                        >
                          <img
                            src={card.img}
                            alt=""
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              opacity: 0.75,
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(to top, rgba(14,13,12,0.92) 0%, transparent 55%)',
                            pointerEvents: 'none',
                          }} />
                          {selected && (
                            <div style={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: '#c8a96e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}
                            >
                              <svg width="11" height="9" viewBox="0 0 11 9" aria-hidden>
                                <path
                                  d="M1 4.5 L4 7.5 L10 1"
                                  fill="none"
                                  stroke="#0e0d0c"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          )}
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: 14,
                            pointerEvents: 'none',
                          }}
                          >
                            <div style={{
                              fontFamily: "'Playfair Display', serif",
                              fontSize: 16,
                              fontWeight: 700,
                              color: '#f0ebe3',
                              lineHeight: 1.2,
                            }}>
                              {card.label}
                            </div>
                            <div style={{
                              fontSize: 10,
                              fontWeight: 500,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              color: '#8a8278',
                              marginTop: 6,
                            }}>
                              {card.sub}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                margin: '8px 0 20px',
              }}
              >
                <div style={{ flex: 1, height: 1, background: '#2a2825' }} />
                <span style={{
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#524e49',
                  whiteSpace: 'nowrap',
                }}>
                  Your vibe profile
                </span>
                <div style={{ flex: 1, height: 1, background: '#2a2825' }} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 28 }}>
                {ARCHETYPE_BARS.map(({ key, label, desc }) => {
                  const pct = totalArchetype === 0 ? 0 : Math.round((archetypeScores[key] / totalArchetype) * 100);
                  return (
                    <div key={key}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#f0ebe3' }}>{label}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#c8a96e' }}>{pct}%</span>
                      </div>
                      <div style={{ height: 4, background: '#2a2825', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: '#c8a96e',
                          borderRadius: 2,
                          transition: 'width 0.55s cubic-bezier(0.16, 1, 0.3, 1)',
                        }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#524e49', marginTop: 6, lineHeight: 1.4 }}>
                        {desc}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedCount > 0 && (
                <>
                  <div style={{
                    background: '#161512',
                    border: '1px solid #2a2825',
                    borderLeft: '3px solid #c8a96e',
                    padding: '18px 20px',
                    marginBottom: 20,
                  }}
                  >
                    <div style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: '#c8a96e',
                      marginBottom: 10,
                    }}>
                      Your neighborhood match
                    </div>
                    <p style={{ fontSize: 13, color: '#8a8278', lineHeight: 1.55, margin: 0 }}>
                      {ARCHETYPE_INSIGHT[dominantKey]}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <button
                      type="button"
                      onClick={handleBuildItinerary}
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        padding: '10px 18px',
                        borderRadius: 4,
                        border: 'none',
                        cursor: 'pointer',
                        background: '#c8a96e',
                        color: '#0e0d0c',
                      }}
                    >
                      Build My Itinerary →
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateVibeResults}
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        padding: '10px 18px',
                        borderRadius: 4,
                        border: '1px solid #2a2825',
                        cursor: 'pointer',
                        background: 'none',
                        color: '#8a8278',
                      }}
                    >
                      Just update my map
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      <EmailGate
        isOpen={showEmailGate}
        homeCity={homeCity}
        destinationCity={translateCity}
        onProceed={handleGateProceed}
        onClose={handleGateClose}
      />

      {itineraryPhase === 'generating' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 125, background: '#0e0d0c',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 22,
          fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
        }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#f0ebe3' }}>
            City<span style={{ color: '#c8a96e' }}>Mirror</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#c8a96e', animation: `cmPulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
            ))}
          </div>
          <div style={{ fontSize: 13, color: '#8a8278', letterSpacing: '0.03em', textAlign: 'center', maxWidth: 320, lineHeight: 1.6 }}>
            Translating {translateCity} into a language you already speak
          </div>
        </div>
      )}

      {itineraryPhase === 'error' && itineraryError && (
        <div style={{
          position: 'fixed',
          top: isMobile ? 72 : 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 126,
          width: 'min(420px, calc(100vw - 32px))',
          padding: '14px 16px',
          borderRadius: 8,
          border: '1px solid rgba(200, 169, 110, 0.35)',
          background: '#161512',
          boxShadow: '0 18px 48px rgba(0,0,0,0.45)',
          color: '#f0ebe3',
          fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
        }}>
          {itineraryError}
        </div>
      )}

      {isMobile ? (
        /* MobileLayout */
        <div style={{
          position: 'fixed',
          inset: 0,
          background: '#0e0d0c',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overscrollBehavior: 'none',
          touchAction: 'pan-y',
          fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
          color: '#f0ebe3',
        }}>
          <nav style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            borderBottom: '1px solid #2a2825',
            background: 'rgba(14,13,12,0.95)',
            zIndex: 20,
            flexShrink: 0,
          }}>
            <button
              type="button"
              onClick={() => onBack()}
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                color: '#8a8278',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ← Back
            </button>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              City<span style={{ color: '#c8a96e' }}>Mirror</span>
            </div>
            <div style={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#f0ebe3',
              background: '#161512',
              border: '1px solid #2a2825',
              borderRadius: 20,
              padding: '4px 10px',
              whiteSpace: 'nowrap',
            }}>
              {abbrevCityPair(homeCity, translateCity)}
            </div>
          </nav>

          <div style={{ position: 'absolute', top: 56, left: 0, right: 0, bottom: 0 }}>
            <div key={mapInstanceKey} ref={mapContainer} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }} />

            {mapMode === 'safety' && neighborhoods.map((n) => {
              const pos = overlayPx[n.id];
              if (!pos) return null;
              return (
                <div
                  key={`${n.id}-safety-m`}
                  onClick={() => select(n.id)}
                  style={{
                    position: 'absolute',
                    left: pos.left,
                    top: pos.top,
                    width: 90,
                    height: 90,
                    marginLeft: -45,
                    marginTop: -45,
                    borderRadius: '50%',
                    background: SAFETY_CIRCLE_BG[n.safety] || SAFETY_CIRCLE_BG.Medium,
                    cursor: 'pointer',
                    zIndex: 5,
                    pointerEvents: 'auto',
                  }}
                />
              );
            })}

            {mapMode === 'vibe' && neighborhoods.map((n) => {
              const pos = overlayPx[n.id];
              if (!pos) return null;
              const op = vibeCircleOpacity(n.vibeMatch);
              return (
                <div
                  key={`${n.id}-vibe-m`}
                  onClick={() => select(n.id)}
                  style={{
                    position: 'absolute',
                    left: pos.left,
                    top: pos.top,
                    marginLeft: -45,
                    marginTop: -45,
                    width: 90,
                    height: 90,
                    borderRadius: '50%',
                    background: '#c8a96e',
                    opacity: op,
                    cursor: 'pointer',
                    zIndex: 5,
                    pointerEvents: 'auto',
                  }}
                />
              );
            })}
          </div>

          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: sheetState === 'detail' ? 680 : 380,
            background: '#0e0d0c',
            borderRadius: '18px 18px 0 0',
            borderTop: '1px solid #2a2825',
            zIndex: 30,
            transition: 'height 0.4s cubic-bezier(0.16,1,0.3,1)',
            overscrollBehavior: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{
              width: 36,
              height: 4,
              background: '#2a2825',
              borderRadius: 2,
              margin: '10px auto 0',
              flexShrink: 0,
            }} />

            {sheetState === 'list' ? (
              <>
                <div style={{ flexShrink: 0, padding: '16px 16px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#524e49', marginBottom: 8 }}>
                    {neighborhoods.length} Neighborhoods Matched
                  </div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                    {translateCity} through a{' '}
                    <em style={{ fontStyle: 'italic', color: '#c8a96e' }}>{homeCity}</em> lens
                  </div>
                  {tasteProfile && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 10,
                      padding: '5px 10px 5px 12px',
                      borderRadius: 4,
                      border: '1px solid #c8a96e',
                    }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 500, color: '#c8a96e', letterSpacing: '0.04em' }}>
                        Sorted by your vibe
                      </span>
                      <button
                        type="button"
                        aria-label="Clear taste profile"
                        onClick={() => setTasteProfile(null)}
                        style={{
                          fontSize: 14,
                          lineHeight: 1,
                          color: '#c8a96e',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0 2px',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile search bar */}
                <div style={{ flexShrink: 0, padding: '0 12px 10px' }}>
                  {/* Direction toggle */}
                  <div style={{ display: 'flex', marginBottom: 8, background: '#0e0d0c', border: '1px solid #2a2825', borderRadius: 4, overflow: 'hidden' }}>
                    {[
                      { label: 'From home', value: 'from_home' },
                      { label: `In ${translateCity}`, value: 'from_destination' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setSearchDirection(opt.value); setSearchError(''); setSearchResult(null); setSearchQuery(''); }}
                        style={{
                          flex: 1, padding: '6px 0', fontSize: 10, fontWeight: 500,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                          fontFamily: "'DM Sans', sans-serif",
                          color: searchDirection === opt.value ? '#f0ebe3' : '#524e49',
                          background: searchDirection === opt.value ? '#1e1c18' : 'transparent',
                          border: 'none', cursor: 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <form
                    onSubmit={(e) => { e.preventDefault(); handleNeighborhoodSearch(searchQuery); }}
                    style={{ display: 'flex', gap: 6 }}
                  >
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setSearchError(''); }}
                      placeholder={searchDirection === 'from_destination'
                        ? `Search a neighborhood in ${translateCity}...`
                        : `Search a neighborhood from ${homeCity}...`}
                      style={{
                        flex: 1, padding: '7px 10px', fontSize: 11,
                        fontFamily: "'DM Sans', sans-serif", color: '#f0ebe3',
                        background: '#0e0d0c', border: '1px solid #2a2825',
                        borderRadius: 4, outline: 'none',
                      }}
                    />
                    <button
                      type="submit"
                      disabled={searchLoading}
                      style={{
                        padding: '7px 10px', fontSize: 11, fontWeight: 600,
                        fontFamily: "'DM Sans', sans-serif",
                        background: searchLoading ? '#2a2825' : '#c8a96e',
                        color: searchLoading ? '#524e49' : '#0e0d0c',
                        border: 'none', borderRadius: 4, cursor: searchLoading ? 'default' : 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      {searchLoading ? '…' : '→'}
                    </button>
                  </form>
                  {searchError && (
                    <div style={{ fontSize: 11, color: '#8a8278', marginTop: 6, lineHeight: 1.4 }}>
                      We couldn't find <em style={{ color: '#f0ebe3' }}>{searchError}</em> —{' '}
                      {searchDirection === 'from_destination'
                        ? `try another neighborhood in ${translateCity}`
                        : `try another from ${homeCity}`}
                    </div>
                  )}
                </div>

                <div style={{ flexShrink: 0, display: 'flex', padding: '0 12px 12px', gap: 0 }}>
                  {[
                    { label: 'Vibe Map', value: 'vibe' },
                    { label: 'Safety Map', value: 'safety' },
                    { label: 'My Vibe', value: '__vibe__' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => (opt.value === '__vibe__' ? setShowVibeQuiz(true) : setMapMode(opt.value))}
                      style={{
                        flex: 1,
                        fontSize: 9,
                        fontWeight: 500,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        padding: '7px 0',
                        border: 'none',
                        borderRadius: 0,
                        cursor: 'pointer',
                        fontFamily: "'DM Sans', sans-serif",
                        color: (opt.value === '__vibe__' ? false : mapMode === opt.value) ? '#f0ebe3' : '#8a8278',
                        background: (opt.value === '__vibe__' ? false : mapMode === opt.value) ? '#1e1c18' : 'transparent',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {/* Mobile search result card */}
                  {searchResult && (
                    <div
                      className="nitem"
                      role="button"
                      tabIndex={0}
                      onClick={() => select('__search__')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select('__search__'); }}
                      style={{
                        padding: '14px 16px', borderTop: '1px solid #2a2825',
                        cursor: 'pointer',
                        background: active === '__search__' ? '#111f1a' : 'rgba(126,200,160,0.04)',
                        borderLeft: active === '__search__' ? '2px solid #7ec8a0' : '2px solid rgba(126,200,160,0.3)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3' }}>
                          {searchResult.madrid}
                        </div>
                        <div style={{
                          fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: '#7ec8a0', background: 'rgba(126,200,160,0.1)',
                          border: '1px solid rgba(126,200,160,0.25)', borderRadius: 3, padding: '2px 7px', flexShrink: 0,
                        }}>
                          Your search
                        </div>
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#7ec8a0', marginBottom: 4 }}>
                        {searchResult.searchDirection === 'from_destination'
                          ? `Feels like ${searchResult.nyc} in ${homeCity}`
                          : `Like ${searchResult.nyc} in ${homeCity}`}
                      </div>
                      <div style={{ fontSize: 11, color: '#8a8278', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {searchResult.copy}
                      </div>
                    </div>
                  )}
                  {mapMode === 'safety' ? (
                    <>
                      <div style={{ padding: '12px 16px', borderTop: '1px solid #2a2825' }}>
                        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#524e49', marginBottom: 10 }}>
                          Safety Rating
                        </div>
                        {[
                          { label: 'Very High', color: '#7ec8a0' },
                          { label: 'High', color: '#a8c87e' },
                          { label: 'Medium', color: '#c8b96e' },
                          { label: 'Low', color: '#c8846e' },
                        ].map((tier) => (
                          <div key={tier.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <div style={{ width: 24, height: 8, borderRadius: 2, background: tier.color, opacity: 0.85, flexShrink: 0 }} />
                            <span style={{ fontSize: 11, color: tier.color, fontWeight: 500 }}>{tier.label}</span>
                          </div>
                        ))}
                      </div>
                      {[...sidebarListNeighborhoods]
                        .sort((a, b) => {
                          const order = { 'Very High': 0, High: 1, Medium: 2, Low: 3 };
                          return (order[a.safety] ?? 4) - (order[b.safety] ?? 4);
                        })
                        .map((n) => (
                          <div
                            key={n.id}
                            className="nitem"
                            role="button"
                            tabIndex={0}
                            onClick={() => select(n.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select(n.id); }}
                            style={{
                              padding: '14px 16px',
                              borderTop: '1px solid #2a2825',
                              cursor: 'pointer',
                              background: active === n.id ? '#161512' : 'transparent',
                              borderLeft: active === n.id ? '2px solid #c8a96e' : '2px solid transparent',
                              position: 'relative',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3' }}>
                                {n.madrid}
                              </span>
                              <SidebarVibeBadges
                                vibeMatch={n.vibeMatch}
                                vibeFit={n.vibeFit ?? 0}
                                tasteProfileActive={!!tasteProfile}
                              />
                            </div>
                            <span style={{
                              display: 'inline-block',
                              fontSize: 10,
                              fontWeight: 600,
                              color: S_COLOR[n.safety],
                              background: S_BG[n.safety],
                              border: `1px solid ${S_BORD[n.safety]}`,
                              borderRadius: 3,
                              padding: '3px 8px',
                              letterSpacing: '0.04em',
                              whiteSpace: 'nowrap',
                            }}>
                              {n.safety}
                            </span>
                          </div>
                        ))}
                    </>
                  ) : (
                    sidebarListNeighborhoods.map((n) => (
                      <div
                        key={n.id}
                        className="nitem"
                        role="button"
                        tabIndex={0}
                        onClick={() => select(n.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select(n.id); }}
                        style={{
                          padding: '14px 16px',
                          borderTop: '1px solid #2a2825',
                          cursor: 'pointer',
                          background: active === n.id ? '#161512' : 'transparent',
                          borderLeft: active === n.id ? '2px solid #c8a96e' : '2px solid transparent',
                          position: 'relative',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3', flex: 1 }}>
                            {n.madrid}
                          </div>
                          <SidebarVibeBadges
                            vibeMatch={n.vibeMatch}
                            vibeFit={n.vibeFit ?? 0}
                            tasteProfileActive={!!tasteProfile}
                          />
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 6 }}>
                          {n.nyc}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#8a8278',
                          lineHeight: 1.45,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {n.copy}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : activeN ? (
              <div className="detail-card-scroll" style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 24,
                position: 'relative',
              }}>
                <button
                  type="button"
                  aria-label="Close detail"
                  onClick={() => {
                    setActive(null);
                    setSheetState('list');
                  }}
                  style={{
                    position: 'absolute',
                    top: 14,
                    right: 16,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#161512',
                    border: '1px solid #2a2825',
                    color: '#8a8278',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    lineHeight: 1,
                    zIndex: 5,
                  }}
                >
                  ×
                </button>

                {photos.length > 0 && (
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: 180,
                    flexShrink: 0,
                    background: '#0e0d0c',
                    marginTop: 8,
                  }}>
                    <img
                      src={photos[Math.min(photoIndex, photos.length - 1)]}
                      alt=""
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        transition: 'opacity 0.25s ease',
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: '55%',
                      background: 'linear-gradient(to top, #161512 0%, rgba(22,21,18,0.65) 40%, transparent 100%)',
                      pointerEvents: 'none',
                    }} />
                    <button
                      type="button"
                      aria-label="Previous photo"
                      onClick={() => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length)}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: 8,
                        transform: 'translateY(-50%)',
                        width: 32,
                        height: 32,
                        borderRadius: 4,
                        background: 'rgba(14,13,12,0.75)',
                        border: '1px solid rgba(200,169,110,0.25)',
                        color: '#c8a96e',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        zIndex: 2,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      aria-label="Next photo"
                      onClick={() => setPhotoIndex((i) => (i + 1) % photos.length)}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        width: 32,
                        height: 32,
                        borderRadius: 4,
                        background: 'rgba(14,13,12,0.75)',
                        border: '1px solid rgba(200,169,110,0.25)',
                        color: '#c8a96e',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        zIndex: 2,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ›
                    </button>
                    <div style={{
                      position: 'absolute',
                      bottom: 10,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 6,
                      zIndex: 2,
                    }}>
                      {photos.map((_, i) => {
                        const current = Math.min(photoIndex, photos.length - 1);
                        return (
                          <button
                            key={i}
                            type="button"
                            aria-label={`Photo ${i + 1}`}
                            onClick={() => setPhotoIndex(i)}
                            style={{
                              width: i === current ? 14 : 5,
                              height: 5,
                              borderRadius: 9999,
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              background: i === current ? '#c8a96e' : 'rgba(200,169,110,0.4)',
                              transition: 'width 0.2s ease, background 0.2s ease',
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #2a2825' }}>
                  <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 8 }}>
                    {homeCity} Equivalent
                  </div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, lineHeight: 1.1, color: '#f0ebe3', marginBottom: 6 }}>
                    {activeN.madrid}
                  </div>
                  <div style={{ fontSize: 12, color: '#8a8278' }}>
                    Feels like <span style={{ color: '#c8a96e', fontWeight: 500 }}>{activeN.nyc}</span> in {homeCity}
                  </div>
                </div>

                {lookupRestaurants(homeCity) && lookupRestaurants(translateCity) && (
                  <div style={{ display: 'flex', borderBottom: '1px solid #2a2825' }}>
                    {['vibe', 'eats'].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setDetailTab(tab)}
                        style={{
                          flex: 1, padding: '10px 0', fontSize: 10, fontWeight: 600,
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          background: 'none', border: 'none',
                          borderBottom: detailTab === tab ? '2px solid #c8a96e' : '2px solid transparent',
                          color: detailTab === tab ? '#c8a96e' : '#524e49',
                          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        {tab === 'vibe' ? 'Vibe' : 'Eats'}
                      </button>
                    ))}
                  </div>
                )}

                {detailTab === 'vibe' ? (
                <div style={{ padding: '16px 16px 0' }}>
                  <p style={{ fontSize: 13, lineHeight: 1.75, color: '#8a8278', marginBottom: 14 }}>{activeN.copy}</p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {activeN.tags.map((t) => (
                      <span key={t} style={{
                        fontSize: 10,
                        fontWeight: 500,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#c8a96e',
                        background: 'rgba(200,169,110,.08)',
                        border: '1px solid rgba(200,169,110,.2)',
                        borderRadius: 3,
                        padding: '4px 10px',
                      }}>{t}</span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #2a2825', borderBottom: '1px solid #2a2825', padding: '14px 0' }}>
                    <div style={{ flex: '0 0 90px', display: 'flex', justifyContent: 'center', borderRight: '1px solid #2a2825' }}>
                      <VibeMeter value={activeN.vibeMatch} />
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #2a2825', padding: '0 10px' }}>
                      <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 7 }}>Safety</div>
                      <div style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 600,
                        color: S_COLOR[activeN.safety],
                        background: S_BG[activeN.safety],
                        border: `1px solid ${S_BORD[activeN.safety]}`,
                        borderRadius: 3,
                        padding: '3px 9px',
                        letterSpacing: '0.04em',
                      }}>
                        {activeN.safety}
                      </div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', padding: '0 10px' }}>
                      <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 6 }}>Price</div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600, color: '#f0ebe3' }}>
                        {activeN.price}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', paddingBottom: 16 }}>
                    {[
                      { label: 'Nightlife', val: activeN.night },
                      { label: 'Walkability', val: activeN.walk },
                    ].map((m, i) => (
                      <div key={m.label} style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '12px 8px 0',
                        borderLeft: i > 0 ? '1px solid #2a2825' : 'none',
                      }}>
                        <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 5 }}>
                          {m.label}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#8a8278' }}>{m.val}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ borderTop: '1px solid #2a2825', padding: '16px 0 0', marginTop: 4 }}>
                    {!feedbackSubmitted ? (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 10 }}>
                          How accurate is this match?
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setFeedbackRating(star)}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: feedbackRating === star ? '1px solid #c8a96e' : '1px solid #2a2825',
                                background: feedbackRating >= star ? 'rgba(200,169,110,0.15)' : 'transparent',
                                color: '#c8a96e',
                                cursor: 'pointer',
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {star}
                            </button>
                          ))}
                        </div>
                        <textarea
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          placeholder="Optional notes…"
                          rows={3}
                          style={{
                            width: '100%',
                            resize: 'vertical',
                            marginBottom: 12,
                            padding: '10px 12px',
                            fontSize: 12,
                            fontFamily: "'DM Sans', sans-serif",
                            color: '#f0ebe3',
                            background: '#0e0d0c',
                            border: '1px solid #2a2825',
                            borderRadius: 4,
                            outline: 'none',
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleFeedbackSubmit}
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            padding: '8px 16px',
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            background: '#c8a96e',
                            color: '#0e0d0c',
                          }}
                        >
                          Submit feedback
                        </button>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: '#8a8278' }}>
                        Thanks — your feedback was sent.
                      </div>
                    )}
                  </div>
                </div>
                ) : (
                  <div style={{ padding: '16px 16px 24px' }}>
                    {TASTE_TIERS.map(({ key, label }) => {
                      const anchor = lookupRestaurants(homeCity)?.[key];
                      const dest = lookupRestaurants(translateCity)?.[key];
                      if (!anchor || !dest) return null;
                      return (
                        <div key={key} style={{
                          background: '#0e0d0c', border: '1px solid #2a2825',
                          borderRadius: 5, padding: '14px 16px', marginBottom: 10,
                        }}>
                          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 10 }}>
                            {label}
                          </div>
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#524e49', marginBottom: 3 }}>
                              {homeCity}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#8a8278' }}>
                              {anchor.name}
                            </div>
                          </div>
                          <div style={{ color: '#c8a96e', fontSize: 12, marginBottom: 6, opacity: 0.6 }}>→</div>
                          <div>
                            <div style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#524e49', marginBottom: 3 }}>
                              {translateCity}
                            </div>
                            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3', marginBottom: 2, lineHeight: 1.2 }}>
                              {dest.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#8a8278', marginBottom: 4 }}>
                              {dest.neighborhood} · {dest.cuisine} · {dest.price}
                            </div>
                            <div style={{ fontSize: 11, color: '#524e49' }}>
                              Feels like <span style={{ color: '#8a8278' }}>{anchor.name}</span> in {homeCity}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 11, color: '#524e49', fontStyle: 'italic', marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                      More restaurants coming soon
                    </div>
                  </div>
                )}

                {photos.length > 0 && (
                  <div style={{
                    padding: '8px 16px 12px',
                    textAlign: 'center',
                    fontSize: 9,
                    color: '#2a2825',
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: '0.04em',
                  }}>
                    Photos via Unsplash
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        /* DesktopLayout */
    <div style={{
      fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      background: '#0e0d0c', color: '#f0ebe3',
      height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>

      {/* NAV */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 56, borderBottom: '1px solid #2a2825',
        flexShrink: 0, background: '#0e0d0c', zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            type="button"
            onClick={() => onBack()}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#8a8278',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ← Back
          </button>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
            City<span style={{ color: '#c8a96e' }}>Mirror</span>
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#161512', border: '1px solid #2a2825', borderRadius: 5, padding: '7px 16px',
        }}>
          <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{homeCity}</span>
          <span style={{ color: '#c8a96e', fontSize: 12, padding: '0 2px' }}>→</span>
          <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{translateCity}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => setShowVibeQuiz(true)}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#c8a96e',
              background: 'none',
              border: '1px solid rgba(200,169,110,0.3)',
              borderRadius: 4,
              padding: '6px 14px',
              cursor: 'pointer',
            }}
          >
            Map My Vibe
          </button>
          <button type="button" style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a8278',
            background: 'none', border: '1px solid #2a2825', borderRadius: 4, padding: '6px 14px', cursor: 'pointer',
          }}>
            My Taste Profile
          </button>
        </div>
      </nav>

      {/* MAIN */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* SIDEBAR */}
        <div style={{
          width: 300, flexShrink: 0, borderRight: '1px solid #2a2825',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #2a2825', flexShrink: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#524e49', marginBottom: 8 }}>
              {neighborhoods.length} Neighborhoods Matched
            </div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 21, fontWeight: 700, lineHeight: 1.2 }}>
              {translateCity} through a{' '}
              <em style={{ fontStyle: 'italic', color: '#c8a96e' }}>{homeCity}</em> lens
            </div>
            {tasteProfile && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 10,
                padding: '5px 10px 5px 12px',
                borderRadius: 4,
                border: '1px solid #c8a96e',
                alignSelf: 'flex-start',
              }}
              >
                <span style={{ fontSize: 10, fontWeight: 500, color: '#c8a96e', letterSpacing: '0.04em' }}>
                  Sorted by your vibe
                </span>
                <button
                  type="button"
                  aria-label="Clear taste profile"
                  onClick={() => setTasteProfile(null)}
                  style={{
                    fontSize: 14,
                    lineHeight: 1,
                    color: '#c8a96e',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0 2px',
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div style={{ fontSize: 12, color: '#8a8278', marginTop: 8, lineHeight: 1.5 }}>
              {mapMode === 'safety'
                ? 'Sorted by safety rating'
                : mapMode === 'vibe'
                  ? 'Sorted by vibe match'
                  : 'Select any neighborhood to explore'}
            </div>
          </div>

          {/* SEARCH BAR */}
          <div style={{ flexShrink: 0, padding: '10px 16px 10px', borderBottom: '1px solid #2a2825' }}>
            {/* Direction toggle */}
            <div style={{ display: 'flex', marginBottom: 8, background: '#0e0d0c', border: '1px solid #2a2825', borderRadius: 4, overflow: 'hidden' }}>
              {[
                { label: 'From home', value: 'from_home' },
                { label: `In ${translateCity}`, value: 'from_destination' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setSearchDirection(opt.value); setSearchError(''); setSearchResult(null); setSearchQuery(''); }}
                  style={{
                    flex: 1, padding: '6px 0', fontSize: 10, fontWeight: 500,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    fontFamily: "'DM Sans', sans-serif",
                    color: searchDirection === opt.value ? '#f0ebe3' : '#524e49',
                    background: searchDirection === opt.value ? '#1e1c18' : 'transparent',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); handleNeighborhoodSearch(searchQuery); }}
              style={{ display: 'flex', gap: 6 }}
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchError(''); }}
                placeholder={searchDirection === 'from_destination'
                  ? `Search a neighborhood in ${translateCity}...`
                  : `Search a neighborhood from ${homeCity}...`}
                style={{
                  flex: 1, padding: '7px 10px', fontSize: 11,
                  fontFamily: "'DM Sans', sans-serif", color: '#f0ebe3',
                  background: '#0e0d0c', border: '1px solid #2a2825',
                  borderRadius: 4, outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={searchLoading}
                style={{
                  padding: '7px 10px', fontSize: 11, fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                  background: searchLoading ? '#2a2825' : '#c8a96e',
                  color: searchLoading ? '#524e49' : '#0e0d0c',
                  border: 'none', borderRadius: 4, cursor: searchLoading ? 'default' : 'pointer',
                  flexShrink: 0, letterSpacing: '0.04em',
                }}
              >
                {searchLoading ? '…' : '→'}
              </button>
            </form>
            {searchError && (
              <div style={{ fontSize: 11, color: '#8a8278', marginTop: 7, lineHeight: 1.4 }}>
                We couldn't find <em style={{ color: '#f0ebe3' }}>{searchError}</em> —{' '}
                {searchDirection === 'from_destination'
                  ? `try another neighborhood in ${translateCity}`
                  : `try another neighborhood from ${homeCity}`}
              </div>
            )}
          </div>

          {/* SIDEBAR LIST — switches based on mapMode */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {/* SEARCH RESULT CARD */}
            {searchResult && (
              <div
                className="nitem"
                onClick={() => select('__search__')}
                style={{
                  padding: '13px 24px', borderBottom: '1px solid #2a2825',
                  cursor: 'pointer',
                  background: active === '__search__' ? '#111f1a' : 'rgba(126,200,160,0.04)',
                  borderLeft: active === '__search__' ? '2px solid #7ec8a0' : '2px solid rgba(126,200,160,0.3)',
                  transition: 'all .15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3' }}>
                    {searchResult.madrid}
                  </div>
                  <div style={{
                    fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: '#7ec8a0', background: 'rgba(126,200,160,0.1)',
                    border: '1px solid rgba(126,200,160,0.25)', borderRadius: 3, padding: '2px 7px',
                    flexShrink: 0, marginLeft: 8,
                  }}>
                    Your search
                  </div>
                </div>
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#7ec8a0', marginBottom: 5 }}>
                  {searchResult.searchDirection === 'from_destination'
                    ? `Feels like ${searchResult.nyc} in ${homeCity}`
                    : `Like ${searchResult.nyc} in ${homeCity}`}
                </div>
                <div style={{
                  fontSize: 12, color: '#8a8278', lineHeight: 1.5,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {searchResult.copy}
                </div>
              </div>
            )}
            {mapMode === 'safety' ? (
              <>
                {/* Safety legend */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #2a2825' }}>
                  <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#524e49', marginBottom: 12 }}>
                    Safety Rating
                  </div>
                  {[
                    { label: 'Very High', color: '#7ec8a0' },
                    { label: 'High', color: '#a8c87e' },
                    { label: 'Medium', color: '#c8b96e' },
                    { label: 'Low', color: '#c8846e' },
                  ].map(tier => (
                    <div key={tier.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 28, height: 10, borderRadius: 2, background: tier.color, opacity: 0.85, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: tier.color, fontWeight: 500 }}>{tier.label}</span>
                    </div>
                  ))}
                </div>
                {/* Safety-sorted neighborhood list */}
                {[...sidebarListNeighborhoods]
                  .sort((a, b) => {
                    const order = { 'Very High': 0, High: 1, Medium: 2, Low: 3 };
                    return (order[a.safety] ?? 4) - (order[b.safety] ?? 4);
                  })
                  .map(n => (
                    <div
                      key={n.id}
                      className="nitem"
                      onClick={() => select(n.id)}
                      style={{
                        padding: '13px 24px', borderBottom: '1px solid #2a2825',
                        cursor: 'pointer',
                        background: active === n.id ? '#161512' : 'transparent',
                        borderLeft: active === n.id ? '2px solid #c8a96e' : '2px solid transparent',
                        transition: 'all .15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3' }}>
                          {n.madrid}
                        </span>
                        <SidebarVibeBadges
                          vibeMatch={n.vibeMatch}
                          vibeFit={n.vibeFit ?? 0}
                          tasteProfileActive={!!tasteProfile}
                        />
                      </div>
                      <span style={{
                        display: 'inline-block',
                        fontSize: 10, fontWeight: 600,
                        color: S_COLOR[n.safety], background: S_BG[n.safety],
                        border: `1px solid ${S_BORD[n.safety]}`,
                        borderRadius: 3, padding: '3px 8px', letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}>
                        {n.safety}
                      </span>
                    </div>
                  ))
                }
              </>
            ) : (
              /* Default neighborhood list */
              sidebarListNeighborhoods.map((n) => (
                <div
                  key={n.id}
                  className="nitem"
                  onClick={() => select(n.id)}
                  style={{
                    padding: '13px 24px', borderBottom: '1px solid #2a2825',
                    cursor: 'pointer',
                    background: active === n.id ? '#161512' : 'transparent',
                    borderLeft: active === n.id ? '2px solid #c8a96e' : '2px solid transparent',
                    transition: 'all .15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3' }}>
                      {n.madrid}
                    </div>
                    <SidebarVibeBadges
                      vibeMatch={n.vibeMatch}
                      vibeFit={n.vibeFit ?? 0}
                      tasteProfileActive={!!tasteProfile}
                    />
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 5 }}>
                    {n.nyc}
                  </div>
                  <div style={{
                    fontSize: 12, color: '#8a8278', lineHeight: 1.5,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {n.copy}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* SIDEBAR CTA */}
          <div style={{
            flexShrink: 0,
            padding: '18px 20px',
            borderTop: '1px solid #2a2825',
            background: '#0e0d0c',
          }}>
            <div style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 14, fontWeight: 700,
              color: '#f0ebe3', marginBottom: 5, lineHeight: 1.25,
            }}>
              Ready to explore {translateCity}?
            </div>
            <div style={{
              fontSize: 11, color: '#524e49', lineHeight: 1.55, marginBottom: 14,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              Build a personalized itinerary based on your taste profile
            </div>
            <button
              type="button"
              onClick={() => setShowVibeQuiz(true)}
              style={{
                width: '100%',
                padding: '10px 0',
                background: '#c8a96e',
                color: '#0e0d0c',
                border: 'none',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Build My Itinerary →
            </button>
          </div>
        </div>

        {/* MAP */}
        <div key={mapInstanceKey} ref={mapContainer} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

          {mapMode === 'safety' && neighborhoods.map((n) => {
            const pos = overlayPx[n.id];
            if (!pos) return null;
            return (
              <div
                key={`${n.id}-safety`}
                onClick={() => select(n.id)}
                style={{
                  position: 'absolute',
                  left: pos.left,
                  top: pos.top,
                  width: 90,
                  height: 90,
                  marginLeft: -45,
                  marginTop: -45,
                  borderRadius: '50%',
                  background: SAFETY_CIRCLE_BG[n.safety] || SAFETY_CIRCLE_BG.Medium,
                  cursor: 'pointer',
                  zIndex: 5,
                  pointerEvents: 'auto',
                }}
              />
            );
          })}

          {mapMode === 'vibe' && neighborhoods.map((n) => {
            const pos = overlayPx[n.id];
            if (!pos) return null;
            const op = vibeCircleOpacity(n.vibeMatch);
            return (
              <div
                key={`${n.id}-vibe`}
                onClick={() => select(n.id)}
                style={{
                  position: 'absolute',
                  left: pos.left,
                  top: pos.top,
                  marginLeft: -45,
                  marginTop: -45,
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: '#c8a96e',
                  opacity: op,
                  cursor: 'pointer',
                  zIndex: 5,
                  pointerEvents: 'auto',
                }}
              />
            );
          })}

          {/* Day-colored neighborhood circles when itinerary is displayed */}
          {itineraryPhase === 'display' && itineraryDayMap.map((day) =>
            day.neighborhoods.map((n) => {
              const pos = overlayPx[n.id];
              if (!pos) return null;
              const color = DAY_COLORS[day.dayIdx % DAY_COLORS.length];
              const isActive = day.dayIdx === itineraryActiveDay;
              return (
                <div
                  key={`${n.id}-day`}
                  style={{
                    position: 'absolute',
                    left: pos.left,
                    top: pos.top,
                    width: 18,
                    height: 18,
                    marginLeft: -9,
                    marginTop: -9,
                    borderRadius: '50%',
                    background: color,
                    opacity: isActive ? 0.95 : 0.45,
                    animation: isActive ? `cmPulse 1.4s ease-in-out infinite` : 'none',
                    zIndex: 6,
                    pointerEvents: 'none',
                    boxShadow: isActive ? `0 0 14px ${color}` : 'none',
                  }}
                />
              );
            })
          )}

          {/* Toggle */}
          <div style={{
            position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', background: '#161512', border: '1px solid #2a2825',
            borderRadius: 5, overflow: 'hidden', zIndex: 10,
          }}>
            {[
              { label: 'Vibe Map', value: 'vibe' },
              { label: 'Safety Map', value: 'safety' },
            ].map((opt, i) => (
              <button
                key={opt.value}
                onClick={() => setMapMode(opt.value)}
                style={{
                  padding: '8px 16px', fontSize: 11, fontWeight: 500,
                  letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: mapMode === opt.value ? '#f0ebe3' : '#8a8278',
                  background: mapMode === opt.value ? '#1e1c18' : 'none',
                  border: 'none',
                  borderLeft: i > 0 ? '1px solid #2a2825' : 'none',
                  cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'color .15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Compass */}
          <div style={{
            position: 'absolute', top: 20, right: 20, width: 36, height: 36,
            borderRadius: '50%', background: '#161512', border: '1px solid #2a2825',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#c8a96e' }}>N</span>
          </div>

          {/* Scale */}
          <div style={{ position: 'absolute', bottom: 20, left: 20, display: 'flex', alignItems: 'center', gap: 8, zIndex: 10 }}>
            <div style={{ width: 60, height: 1, background: '#524e49', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: -2, width: 1, height: 5, background: '#524e49' }} />
              <div style={{ position: 'absolute', right: 0, top: -2, width: 1, height: 5, background: '#524e49' }} />
            </div>
            <span style={{ fontSize: 10, letterSpacing: '0.08em', color: '#524e49' }}>1 km</span>
          </div>

          {/* DETAIL CARD */}
          {activeN && !itineraryPhase && (
            <div
              key={activeN.id}
              className="detail-card-scroll"
              style={{
                position: 'absolute', bottom: 28, right: 28, width: 400,
                maxHeight: 'calc(100vh - 120px)',
                overflowX: 'hidden',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#2a2825 transparent',
                background: '#161512', border: '1px solid #2d2b27',
                borderRadius: 6, zIndex: 20,
                animation: 'cardIn .32s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              {photos.length > 0 && (
                <div style={{
                  position: 'relative', width: '100%', height: 220, flexShrink: 0,
                  background: '#0e0d0c',
                }}>
                  <img
                    src={photos[Math.min(photoIndex, photos.length - 1)]}
                    alt=""
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      transition: 'opacity 0.25s ease',
                    }}
                  />
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%',
                    background: 'linear-gradient(to top, #161512 0%, rgba(22,21,18,0.65) 40%, transparent 100%)',
                    pointerEvents: 'none',
                  }} />
                  <button
                    type="button"
                    aria-label="Previous photo"
                    onClick={() => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length)}
                    style={{
                      position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)',
                      width: 32, height: 32, borderRadius: 4,
                      background: 'rgba(14,13,12,0.75)',
                      border: '1px solid rgba(200,169,110,0.25)',
                      color: '#c8a96e', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 2,
                      fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1,
                    }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    aria-label="Next photo"
                    onClick={() => setPhotoIndex((i) => (i + 1) % photos.length)}
                    style={{
                      position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
                      width: 32, height: 32, borderRadius: 4,
                      background: 'rgba(14,13,12,0.75)',
                      border: '1px solid rgba(200,169,110,0.25)',
                      color: '#c8a96e', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 2,
                      fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1,
                    }}
                  >
                    ›
                  </button>
                  <div style={{
                    position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex',
                    justifyContent: 'center', alignItems: 'center', gap: 6, zIndex: 2,
                  }}>
                    {photos.map((_, i) => {
                      const current = Math.min(photoIndex, photos.length - 1);
                      return (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Photo ${i + 1}`}
                          onClick={() => setPhotoIndex(i)}
                          style={{
                            width: i === current ? 14 : 5,
                            height: 5,
                            borderRadius: 9999,
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            background: i === current ? '#c8a96e' : 'rgba(200,169,110,0.4)',
                            transition: 'width 0.2s ease, background 0.2s ease',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid #2a2825' }}>
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 8 }}>
                  {homeCity} Equivalent
                </div>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: '#f0ebe3', marginBottom: 5 }}>
                  {activeN.madrid}
                </div>
                <div style={{ fontSize: 12, color: '#8a8278' }}>
                  Feels like <span style={{ color: '#c8a96e', fontWeight: 500 }}>{activeN.nyc}</span> in {homeCity}
                </div>
              </div>

              {lookupRestaurants(homeCity) && lookupRestaurants(translateCity) && (
                <div style={{ display: 'flex', borderBottom: '1px solid #2a2825' }}>
                  {['vibe', 'eats'].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setDetailTab(tab)}
                      style={{
                        flex: 1, padding: '10px 0', fontSize: 10, fontWeight: 600,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        background: 'none', border: 'none',
                        borderBottom: detailTab === tab ? '2px solid #c8a96e' : '2px solid transparent',
                        color: detailTab === tab ? '#c8a96e' : '#524e49',
                        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {tab === 'vibe' ? 'Vibe' : 'Eats'}
                    </button>
                  ))}
                </div>
              )}

              {detailTab === 'vibe' ? (
              <div style={{ padding: '16px 22px 16px' }}>
                <p style={{ fontSize: 13, lineHeight: 1.75, color: '#8a8278', marginBottom: 14 }}>{activeN.copy}</p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {activeN.tags.map((t) => (
                    <span key={t} style={{
                      fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: '#c8a96e', background: 'rgba(200,169,110,.08)',
                      border: '1px solid rgba(200,169,110,.2)', borderRadius: 3, padding: '4px 10px',
                    }}>{t}</span>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #2a2825', borderBottom: '1px solid #2a2825', padding: '14px 0' }}>
                  <div style={{ flex: '0 0 90px', display: 'flex', justifyContent: 'center', borderRight: '1px solid #2a2825' }}>
                    <VibeMeter value={activeN.vibeMatch} />
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #2a2825', padding: '0 10px' }}>
                    <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 7 }}>Safety</div>
                    <div style={{
                      display: 'inline-block', fontSize: 11, fontWeight: 600,
                      color: S_COLOR[activeN.safety], background: S_BG[activeN.safety],
                      border: `1px solid ${S_BORD[activeN.safety]}`,
                      borderRadius: 3, padding: '3px 9px', letterSpacing: '0.04em',
                    }}>
                      {activeN.safety}
                    </div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '0 10px' }}>
                    <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 6 }}>Price</div>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 600, color: '#f0ebe3' }}>
                      {activeN.price}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', paddingBottom: 16 }}>
                  {[
                    { label: 'Nightlife', val: activeN.night },
                    { label: 'Walkability', val: activeN.walk },
                  ].map((m, i) => (
                    <div key={m.label} style={{
                      flex: 1, textAlign: 'center', padding: '12px 8px 0',
                      borderLeft: i > 0 ? '1px solid #2a2825' : 'none',
                    }}>
                      <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 5 }}>
                        {m.label}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#8a8278' }}>{m.val}</div>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: '1px solid #2a2825', padding: '16px 0 0', marginTop: 4 }}>
                  {!feedbackSubmitted ? (
                    <>
                      <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#524e49', marginBottom: 10 }}>
                        How accurate is this match?
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFeedbackRating(star)}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 4,
                              border: feedbackRating === star ? '1px solid #c8a96e' : '1px solid #2a2825',
                              background: feedbackRating >= star ? 'rgba(200,169,110,0.15)' : 'transparent',
                              color: '#c8a96e',
                              cursor: 'pointer',
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {star}
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Optional notes…"
                        rows={3}
                        style={{
                          width: '100%',
                          resize: 'vertical',
                          marginBottom: 12,
                          padding: '10px 12px',
                          fontSize: 12,
                          fontFamily: "'DM Sans', sans-serif",
                          color: '#f0ebe3',
                          background: '#0e0d0c',
                          border: '1px solid #2a2825',
                          borderRadius: 4,
                          outline: 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleFeedbackSubmit}
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          padding: '8px 16px',
                          borderRadius: 4,
                          border: 'none',
                          cursor: 'pointer',
                          background: '#c8a96e',
                          color: '#0e0d0c',
                        }}
                      >
                        Submit feedback
                      </button>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: '#8a8278' }}>
                      Thanks — your feedback was sent.
                    </div>
                  )}
                </div>
              </div>
              ) : (
                <div style={{ padding: '16px 22px 24px' }}>
                  {TASTE_TIERS.map(({ key, label }) => {
                    const anchor = lookupRestaurants(homeCity)?.[key];
                    const dest = lookupRestaurants(translateCity)?.[key];
                    if (!anchor || !dest) return null;
                    return (
                      <div key={key} style={{
                        background: '#0e0d0c', border: '1px solid #2a2825',
                        borderRadius: 5, padding: '14px 16px', marginBottom: 10,
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 10 }}>
                          {label}
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#524e49', marginBottom: 3 }}>
                            {homeCity}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#8a8278' }}>
                            {anchor.name}
                          </div>
                        </div>
                        <div style={{ color: '#c8a96e', fontSize: 12, marginBottom: 6, opacity: 0.6 }}>→</div>
                        <div>
                          <div style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#524e49', marginBottom: 3 }}>
                            {translateCity}
                          </div>
                          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 600, color: '#f0ebe3', marginBottom: 2, lineHeight: 1.2 }}>
                            {dest.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#8a8278', marginBottom: 4 }}>
                            {dest.neighborhood} · {dest.cuisine} · {dest.price}
                          </div>
                          <div style={{ fontSize: 11, color: '#524e49' }}>
                            Feels like <span style={{ color: '#8a8278' }}>{anchor.name}</span> in {homeCity}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 11, color: '#524e49', fontStyle: 'italic', marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                    More restaurants coming soon
                  </div>
                </div>
              )}
              {photos.length > 0 && (
                <div style={{
                  padding: '8px 22px 12px',
                  textAlign: 'center',
                  fontSize: 9,
                  color: '#2a2825',
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.04em',
                }}>
                  Photos via Unsplash
                </div>
              )}
            </div>
          )}
          {/* ITINERARY DISPLAY CARD (collapsed, in-panel) */}
          {itineraryPhase === 'display' && !itineraryExpanded && (
            <div
              ref={itineraryCardRef}
              className="detail-card-scroll"
              onScroll={() => {
                if (!itineraryCardRef.current) return;
                const container = itineraryCardRef.current;
                const containerRect = container.getBoundingClientRect();
                const blocks = container.querySelectorAll('[data-day-idx]');
                let topVisible = null;
                for (const block of blocks) {
                  const rect = block.getBoundingClientRect();
                  if (rect.top <= containerRect.top + 100) topVisible = parseInt(block.dataset.dayIdx, 10);
                }
                if (topVisible !== null && topVisible !== itineraryActiveDay) setItineraryActiveDay(topVisible);
              }}
              style={{
                position: 'absolute', bottom: 28, right: 28, width: 400,
                maxHeight: 'calc(100vh - 120px)',
                overflowY: 'auto', overflowX: 'hidden',
                scrollbarWidth: 'thin', scrollbarColor: '#2a2825 transparent',
                background: '#161512', border: '1px solid #2d2b27',
                borderRadius: 6, zIndex: 20,
                animation: 'cardIn .32s cubic-bezier(0.16,1,0.3,1)',
                fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
              }}
            >
              {/* Sticky header */}
              <div style={{
                position: 'sticky', top: 0, zIndex: 2,
                background: '#161512', padding: '13px 16px 12px',
                borderBottom: '1px solid #2a2825',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <button
                    type="button"
                    onClick={() => { setItineraryPhase(null); setItineraryExpanded(false); }}
                    style={{
                      fontSize: 10, color: '#8a8278', background: 'none', border: 'none',
                      cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase',
                      fontFamily: "'DM Sans', sans-serif", padding: 0,
                    }}
                  >
                    ← Neighborhoods
                  </button>
                  <button
                    type="button"
                    aria-label="Expand to full screen"
                    title="Full screen"
                    onClick={() => setItineraryExpanded(true)}
                    style={{
                      width: 26, height: 26, borderRadius: 4,
                      background: '#0e0d0c', border: '1px solid #2a2825',
                      color: '#524e49', cursor: 'pointer', padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, lineHeight: 1,
                    }}
                  >
                    ⤢
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#f0ebe3', letterSpacing: '-0.01em' }}>
                    Your {translateCity} Itinerary · {itineraryDayMap.length} Days
                  </span>
                  {/* Day indicator dots */}
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {parseItineraryDays(itinerary).map((day) => {
                      const color = DAY_COLORS[day.dayIdx % DAY_COLORS.length];
                      const isActive = day.dayIdx === itineraryActiveDay;
                      return (
                        <button
                          key={day.dayIdx}
                          type="button"
                          onClick={() => {
                            setItineraryActiveDay(day.dayIdx);
                            if (itineraryCardRef.current) {
                              const block = itineraryCardRef.current.querySelector(`[data-day-idx="${day.dayIdx}"]`);
                              if (block) block.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          style={{
                            width: isActive ? 16 : 6,
                            height: 6,
                            borderRadius: 9999,
                            background: color,
                            opacity: isActive ? 1 : 0.4,
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            transition: 'width 0.2s ease, opacity 0.2s ease',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* Itinerary body — day-grouped with scroll detection anchors */}
              <div style={{ padding: '14px 16px 28px' }}>
                {parseItineraryDays(itinerary).map((day) => {
                  const dayColor = DAY_COLORS[day.dayIdx % DAY_COLORS.length];
                  const isActive = day.dayIdx === itineraryActiveDay;
                  return (
                    <div key={day.dayIdx} data-day-idx={day.dayIdx}>
                      {day.lines.map((line, i) => {
                        if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
                        if (line === '---') return <div key={i} style={{ height: 1, background: '#2a2825', margin: '16px 0' }} />;
                        if (line === 'Safe travels. You know this city now.') {
                          return (
                            <div key={i} style={{
                              fontFamily: "'Playfair Display', serif",
                              fontSize: 14, fontStyle: 'italic',
                              color: '#c8a96e', marginTop: 20, textAlign: 'center', lineHeight: 1.4,
                            }}>
                              {line}
                            </div>
                          );
                        }
                        if (/^DAY \d+/.test(line)) {
                          return (
                            <div key={i} style={{
                              fontFamily: "'Playfair Display', serif",
                              fontSize: 16, fontWeight: 700,
                              color: isActive ? dayColor : '#8a8278',
                              marginTop: day.dayIdx === 0 ? 0 : 22,
                              marginBottom: 3, lineHeight: 1.2,
                              display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dayColor, flexShrink: 0 }} />
                              {line}
                            </div>
                          );
                        }
                        if (/^\d+:\d+/.test(line) || /^(Morning|Afternoon|Evening|Night) —/.test(line)) {
                          return (
                            <div key={i} style={{
                              fontSize: 11, fontWeight: 600, color: '#c8a96e',
                              marginTop: 12, marginBottom: 2, letterSpacing: '0.02em',
                            }}>
                              {line}
                            </div>
                          );
                        }
                        return (
                          <p key={i} style={{ fontSize: 12, lineHeight: 1.75, color: '#8a8278', margin: '0 0 2px' }}>
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ITINERARY EXPANDED OVERLAY */}
          {itineraryPhase === 'display' && itineraryExpanded && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 130, background: '#0e0d0c',
              display: 'flex', flexDirection: 'column', overflowY: 'auto',
              fontFamily: "'DM Sans','Helvetica Neue',sans-serif", color: '#f0ebe3',
            }}>
              <div style={{
                position: 'sticky', top: 0, zIndex: 2,
                background: 'rgba(14,13,12,0.97)', padding: '14px 28px',
                borderBottom: '1px solid #2a2825',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <button
                  type="button"
                  onClick={() => { setItineraryPhase(null); setItineraryExpanded(false); }}
                  style={{
                    fontSize: 10, color: '#8a8278', background: 'none', border: 'none',
                    cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase',
                    fontFamily: "'DM Sans', sans-serif", padding: 0,
                  }}
                >
                  ← Neighborhoods
                </button>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>
                  City<span style={{ color: '#c8a96e' }}>Mirror</span>
                </div>
                <button
                  type="button"
                  onClick={() => setItineraryExpanded(false)}
                  style={{
                    fontSize: 10, color: '#8a8278', background: 'none',
                    border: '1px solid #2a2825', borderRadius: 4,
                    cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase',
                    fontFamily: "'DM Sans', sans-serif", padding: '5px 10px',
                  }}
                >
                  ⤡ Collapse
                </button>
              </div>
              <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 28px 80px', width: '100%' }}>
                {renderItineraryLines(itinerary, false)}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
      )}
    </>
  );
}
